import { Injectable, NgZone } from '@angular/core';
import { GoogleGenAI, Modality } from '@google/genai';
import { BehaviorSubject, Observable, Subscription } from 'rxjs';
import { ApiService } from '../api.service';
import { McpContextService } from '../mcp-context.service';
import { McpRealtimeStatus, McpTranscriptEntry } from '../mcp-realtime.service';
import { McpTraceService } from '../mcp-trace.service';
import { PageActionService } from '../page-action.service';
import { McpIntentRouterService } from '../mcp-intent-router.service';
import { RealtimeSegment } from './realtime-segment.types';

@Injectable({ providedIn: 'root' })
export class GeminiRealtimeSegment implements RealtimeSegment {
    readonly id = 'gemini' as const;

    private readonly _status = new BehaviorSubject<McpRealtimeStatus>('idle');
    private readonly _transcript = new BehaviorSubject<McpTranscriptEntry[]>([]);
    private readonly _aiSpeaking = new BehaviorSubject<boolean>(false);
    private readonly _micMuted = new BehaviorSubject<boolean>(true);
    private _session: any = null;
    private _micStream: MediaStream | null = null;
    private _inputAudioCtx: AudioContext | null = null;
    private _inputSource: MediaStreamAudioSourceNode | null = null;
    private _inputProcessor: ScriptProcessorNode | null = null;
    private _outputAudioCtx: AudioContext | null = null;
    private _nextPlaybackTime = 0;
    private _connectTimeout: any = null;
    private _connectAttemptId = 0;
    private _lastInputTranscript = '';
    private _lastOutputTranscript = '';
    private _toolCallInFlight = false;
    private _assistantTurnActive = false;
    private _healthInterval: any = null;
    private _lastServerMessageAt = 0;
    private _lastAudioSentAt = 0;
    private _lastUserSpeechAt = 0;
    private _lastModelResponseAt = 0;
    private _silenceNudgeTimer: any = null;
    private _silenceNudgeCount = 0;
    private _sessionRefreshTimer: any = null;
    private _sessionStartedAt = 0;
    private _autoReconnect = true;
    private _wakeWordRecognition: any = null;
    private _pendingUnmuteAfterReconnect = false;
    private _reconnectTimer: any = null;
    private _lastWatchdogRecoveryAt = 0;
    private _anonymousUserTurns = 0;
    private _playbackEndTimer: any = null;
    /** After end_conversation the next socket open waits for "Yo". */
    private _requireWakeWordOnConnect = false;
    private _micStartTimer: any = null;

    // Live board sync: while a visit is active, mirror kiosk data changes into
    // the conversation so the model never answers from a stale snapshot.
    private _liveContextSub: Subscription | null = null;
    private _liveContextTimer: any = null;
    private _lastLiveContextPush = 0;
    private _lastLiveContextText = '';

    private static readonly WAKE_WORDS = ['yo', 'computer', 'hey assistant', 'a assistant', 'hey system', 'hey gemini'];

    private static readonly HEALTH_CHECK_MS = 5000;
    private static readonly STUCK_THRESHOLD_MS = 10000;
    private static readonly SILENCE_NUDGE_MS = 7000;
    private static readonly MAX_SILENCE_NUDGES = 2;
    private static readonly SESSION_REFRESH_MS = 13 * 60 * 1000; // refresh at 13 min (before 15 min limit)
    // Coalesce a burst of board changes into at most one live push per window.
    private static readonly LIVE_CONTEXT_MIN_INTERVAL_MS = 2500;
    /** On shared kiosk, reconnect after this many user turns to avoid context bloat. */
    private static readonly KIOSK_TURN_REFRESH = 6;
    /** Brief gap after speaker output before accepting mic again. */
    private static readonly POST_PLAYBACK_GAP_S = 0.3;
    private static readonly TOOL_DISPATCH_TIMEOUT_MS = 12000;
    private static readonly TOOL_DISPATCH_SLOW_TIMEOUT_MS = 20000;

    constructor(
        private readonly zone: NgZone,
        private readonly api: ApiService,
        private readonly pageActionService: PageActionService,
        private readonly contextService: McpContextService,
        private readonly traceService: McpTraceService,
        private readonly intentRouter: McpIntentRouterService
    ) {}

    get status$(): Observable<McpRealtimeStatus> {
        return this._status.asObservable();
    }

    get transcript$(): Observable<McpTranscriptEntry[]> {
        return this._transcript.asObservable();
    }

    get aiSpeaking$(): Observable<boolean> {
        return this._aiSpeaking.asObservable();
    }

    get micMuted$(): Observable<boolean> {
        return this._micMuted.asObservable();
    }

    /** Kiosk: false = open mic when session connects; true = wait for "Yo". */
    prepareKioskSession(requireWakeWord: boolean): void {
        this._requireWakeWordOnConnect = requireWakeWord;
    }

    async start(): Promise<void> {
        if (this._status.value === 'connecting' || this._status.value === 'active') {
            return;
        }
        this._autoReconnect = true;

        const snapshot = this.contextService.getSnapshot();
        if (!this.canStartOnCurrentPage(snapshot)) {
            this._status.next('idle');
            this.addTranscript(
                'system',
                'Gemini assistant is available on mapped pages only (Receiving, Shipping, Printer View).'
            );
            return;
        }

        this._status.next('connecting');
        const attemptId = ++this._connectAttemptId;
        this.armConnectTimeout(attemptId);

        try {
            const promptContext = this.contextService.buildPromptContext(snapshot);

            const cfg: any = await this.api
                .postApi('mcp/gemini/live-config', {
                    context: {
                        prompt_context: promptContext,
                        route: snapshot.route,
                        page_label: snapshot.pageLabel,
                        user_name: snapshot.userName,
                        user_role: snapshot.role,
                        anonymous: snapshot.anonymous,
                    },
                })
                .toPromise();

            if (!cfg?.api_key || !cfg?.model) {
                throw new Error('Invalid Gemini live config response');
            }

            const ai = new GoogleGenAI({
                apiKey: cfg.api_key,
            });
            const voiceName = String(cfg.voice ?? 'Orus');
            this._session = await ai.live.connect({
                model: String(cfg.model),
                config: {
                    responseModalities: [Modality.AUDIO],
                    speechConfig: { voiceConfig: { prebuiltVoiceConfig: { voiceName } } },
                    systemInstruction: String(cfg.system_instruction ?? ''),
                    tools: Array.isArray(cfg.tools) ? cfg.tools : [],
                    outputAudioTranscription: {},
                    inputAudioTranscription: {},
                },
                callbacks: {
                    onopen: () => {
                        this.zone.run(() => {
                            if (attemptId !== this._connectAttemptId) return;
                            this.clearConnectTimeout();
                            this._sessionStartedAt = Date.now();
                            this._status.next('active');
                            this._aiSpeaking.next(false);
                            this.ensureOutputAudioContext();
                            this.armSessionRefresh();
                            this.subscribeLiveContext();

                            if (this._pendingUnmuteAfterReconnect) {
                                this._pendingUnmuteAfterReconnect = false;
                                this._micMuted.next(false);
                                this.stopWakeWordListener();
                                this.addTranscript('system', 'Session refreshed — still listening.');
                            } else {
                                // Land asleep — start the wake-word listener
                                // directly (setMicMuted is a no-op when the
                                // muted value hasn't changed).
                                this._requireWakeWordOnConnect = false;
                                this._micMuted.next(true);
                                this.startWakeWordListener();
                                this.addTranscript('system', 'Ready — say "Yo" to begin.');
                            }
                        });
                    },
                    onmessage: (e: any) => this.handleServerMessage(e),
                    onerror: (e: any) => {
                        this.zone.run(() => {
                            if (attemptId !== this._connectAttemptId) return;
                            this.clearConnectTimeout();
                            this.cancelSessionRefresh();
                            this.cancelSilenceNudge();
                            this.stopHealthCheck();
                            console.error('[Gemini] onerror:', e);
                            this._session = null;
                            this._toolCallInFlight = false;
                            this._assistantTurnActive = false;
                            this._status.next('idle');
                            this.addTranscript('system', 'Gemini connection error — reconnecting...');
                            this.scheduleAutoReconnect();
                        });
                    },
                    onclose: (ev: any) => {
                        this.zone.run(() => {
                            if (attemptId !== this._connectAttemptId) return;
                            this.clearConnectTimeout();
                            this.cancelSessionRefresh();
                            this.cancelSilenceNudge();
                            this.stopHealthCheck();
                            console.warn('[Gemini] session closed:', ev?.code, ev?.reason);
                            this._session = null;
                            this._aiSpeaking.next(false);
                            this._toolCallInFlight = false;
                            this._assistantTurnActive = false;
                            this._status.next('idle');
                            if (this._autoReconnect) {
                                this.addTranscript('system', 'Session expired — refreshing...');
                                this.scheduleAutoReconnect();
                            }
                        });
                    },
                },
            });

            if (attemptId !== this._connectAttemptId) return;
            // Acquire the microphone UP FRONT so the browser mic indicator
            // appears immediately and the stream is ready when the wake word
            // fires. We gate whether audio is SENT with _micMuted; the stream
            // itself stays open for the whole session.
            try {
                await this.startMicStreaming();
            } catch (err) {
                console.error('[Gemini] mic acquisition failed:', err);
                this.addTranscript(
                    'system',
                    'Microphone permission was denied — allow the mic in the browser and reload.',
                );
            }
            this.startHealthCheck();
        } catch (err: any) {
            if (attemptId !== this._connectAttemptId) return;
            this.clearConnectTimeout();
            this._status.next('error');
            this.addTranscript('system', 'Gemini failed to start. Please retry.');
            this.traceService.log(
                'gemini_start_failed',
                { error: err?.message ?? String(err) },
                { detail: 'Failed to start Gemini Live' }
            );
        }
    }

    async prewarm(): Promise<void> {
        if (this._status.value === 'active' || this._status.value === 'connecting') {
            return;
        }
        this._autoReconnect = true;
        await this.start();
    }

    stop(): void {
        this._autoReconnect = false;
        this.teardown();
        this._status.next('idle');
        this._micMuted.next(true);
    }

    private teardown(): void {
        this._connectAttemptId++;
        this.clearConnectTimeout();
        this.stopHealthCheck();
        this.cancelSilenceNudge();
        this.cancelSessionRefresh();
        this.unsubscribeLiveContext();
        this.stopWakeWordListener();
        if (this._reconnectTimer) {
            clearTimeout(this._reconnectTimer);
            this._reconnectTimer = null;
        }
        if (this._micStartTimer) {
            clearTimeout(this._micStartTimer);
            this._micStartTimer = null;
        }
        try {
            this._session?.close?.();
        } catch {}
        this._session = null;
        this.stopMicStreaming();
        this.closeOutputAudioContext();
        this._toolCallInFlight = false;
        this._assistantTurnActive = false;
        this._aiSpeaking.next(false);
        this._status.next('idle');
    }

    private async reconnect(): Promise<void> {
        const wasMuted = this._micMuted.value;
        this.traceService.log('gemini_reconnect', { wasMuted }, { detail: 'Auto-reconnecting Gemini session' });
        this._pendingUnmuteAfterReconnect = !wasMuted;
        this.teardown();
        this._autoReconnect = true;
        await this.start();
    }

    private scheduleAutoReconnect(): void {
        if (this._reconnectTimer) return;
        const wasMuted = this._micMuted.value;
        this._pendingUnmuteAfterReconnect = !wasMuted;
        this._reconnectTimer = setTimeout(() => {
            this._reconnectTimer = null;
            if (this._status.value === 'active' || this._status.value === 'connecting') return;
            this._autoReconnect = true;
            this.start().catch((err) => {
                console.error('[Gemini] auto-reconnect failed:', err);
            });
        }, 1500);
    }

    private armSessionRefresh(): void {
        this.cancelSessionRefresh();
        this._sessionRefreshTimer = setTimeout(() => {
            if (this._status.value !== 'active') return;
            this.traceService.log('gemini_session_refresh', {},
                { detail: `Refreshing session (${Math.round(GeminiRealtimeSegment.SESSION_REFRESH_MS / 60000)}min limit)` });
            this.addTranscript('system', 'Refreshing session...');
            this.reconnect().catch((err) => {
                console.error('[Gemini] session refresh failed:', err);
            });
        }, GeminiRealtimeSegment.SESSION_REFRESH_MS);
    }

    private cancelSessionRefresh(): void {
        if (this._sessionRefreshTimer) {
            clearTimeout(this._sessionRefreshTimer);
            this._sessionRefreshTimer = null;
        }
    }

    setMicMuted(muted: boolean): void {
        if (this._micMuted.value === muted) return;
        this._micMuted.next(muted);
        if (this._micStartTimer) {
            clearTimeout(this._micStartTimer);
            this._micStartTimer = null;
        }
        // Mic stream is kept open for the whole session; we just gate whether
        // audio is sent (see the audio callback in startMicStreaming). While
        // muted, layer the wake-word listener on top to detect "Yo".
        if (muted) {
            this.playMuteTone();
            this.startWakeWordListener();
        } else {
            this.stopWakeWordListener();
        }
    }

    /**
     * Inject the fresh kiosk board (full order lists) into the live Gemini
     * session before the wake greeting. Gemini bakes systemInstruction at
     * connect time, so we push an explicit context turn on each "Yo".
     */
    private injectVisitContextBeforeGreeting(): void {
        if (!this._session || this._status.value !== 'active') {
            return;
        }
        try {
            const snapshot = this.contextService.getSnapshot();
            const board = this.contextService.buildPromptContext(snapshot);
            this._session.sendClientContent({
                turns:
                    `[VISIT START — CURRENT KIOSK BOARD]\n${board}\n\n` +
                    'The above is the complete current board. Use it to find orders by #, name, customer, or creator/trader. Do NOT read the full list aloud unless asked.',
                turnComplete: true,
            });
            // The full board just went in — baseline the live tracker so we
            // don't immediately re-push the same summary as a redundant update.
            this._lastLiveContextText = String(snapshot.dataSummary ?? '').trim();
            this._lastLiveContextPush = Date.now();
        } catch (err) {
            console.warn('[Gemini] visit context injection failed:', err);
        }
    }

    /**
     * Subscribe to kiosk context changes for the life of the session. Each emit
     * signals board data may have changed; pushLiveContextUpdate decides
     * whether/when to actually stream it in.
     */
    private subscribeLiveContext(): void {
        this.unsubscribeLiveContext();
        this._liveContextSub = this.contextService.contextUpdate$.subscribe(() => {
            this.zone.run(() => this.pushLiveContextUpdate());
        });
    }

    private unsubscribeLiveContext(): void {
        if (this._liveContextSub) {
            try { this._liveContextSub.unsubscribe(); } catch {}
            this._liveContextSub = null;
        }
        if (this._liveContextTimer) {
            clearTimeout(this._liveContextTimer);
            this._liveContextTimer = null;
        }
    }

    /**
     * Stream the freshest board into the LIVE conversation when kiosk data
     * changes mid-visit (order received/shipped, new arrival, poller refresh).
     *
     * Sent with `turnComplete: false` so Gemini absorbs it as context WITHOUT
     * generating a spoken turn — the equivalent of a silent system note. Only
     * runs while the mic is live (an actual visit); before wake word the full
     * board is re-injected on "Yo". Deferred while a turn is in flight and
     * throttled so a burst of changes collapses into one push.
     */
    private pushLiveContextUpdate(): void {
        if (!this._session || this._status.value !== 'active' || this._micMuted.value) {
            return;
        }
        // Don't inject mid-exchange; retry once the current turn settles.
        if (this._assistantTurnActive || this._toolCallInFlight || this._aiSpeaking.value) {
            this.scheduleLiveContextRetry(GeminiRealtimeSegment.LIVE_CONTEXT_MIN_INTERVAL_MS);
            return;
        }
        const elapsed = Date.now() - this._lastLiveContextPush;
        if (elapsed < GeminiRealtimeSegment.LIVE_CONTEXT_MIN_INTERVAL_MS) {
            this.scheduleLiveContextRetry(GeminiRealtimeSegment.LIVE_CONTEXT_MIN_INTERVAL_MS - elapsed);
            return;
        }
        const snapshot = this.contextService.getSnapshot();
        const summary = String(snapshot.dataSummary ?? '').trim();
        if (!summary || summary === this._lastLiveContextText) {
            return;
        }
        try {
            this._session.sendClientContent({
                turns: `[BOARD UPDATE — kiosk data changed since your last snapshot; use this going forward]\n${summary}`,
                turnComplete: false,
            });
            this._lastLiveContextPush = Date.now();
            this._lastLiveContextText = summary;
        } catch (err) {
            console.warn('[Gemini] live context push failed:', err);
        }
    }

    private scheduleLiveContextRetry(delay: number): void {
        if (this._liveContextTimer) {
            return;
        }
        this._liveContextTimer = setTimeout(() => {
            this._liveContextTimer = null;
            this.zone.run(() => this.pushLiveContextUpdate());
        }, Math.max(250, delay));
    }

    /**
     * On wake, immediately have the assistant say a short hello so the user gets
     * instant audible confirmation it's listening (instead of silence). Only
     * fires when a live session is active.
     */
    private requestWakeGreeting(): void {
        if (!this._session || this._status.value !== 'active') {
            return;
        }
        try {
            this._aiSpeaking.next(true);
            this.armSilenceNudge();
            this._session.sendClientContent({
                turns:
                    'The person just activated you with the wake word. Right now, before they say anything else, greet them out loud with a short, warm, name-free hello such as "Hey, how can I help?" or "Hi there — what can I do for you?" so they immediately know you are listening. Keep it to one brief sentence.',
                turnComplete: true,
            });
        } catch (err) {
            console.error('[Gemini] wake greeting error:', err);
        }
    }

    toggleMic(): void {
        this.setMicMuted(!this._micMuted.value);
    }

    /**
     * End the current visit and return to the wake-word "asleep" state — the
     * same place a fresh start lands. On a shared kiosk each visitor must get a
     * clean slate, so we mute immediately (the goodbye audio still plays), wipe
     * the on-screen transcript + turn state, then reconnect a brand-new session
     * so the previous person's order/name never carries over. The next person
     * says the wake word ("Yo") to begin again.
     */
    private endConversation(): void {
        this.contextService.notifyVisitEnded();
        this._anonymousUserTurns = 0;
        this._requireWakeWordOnConnect = true;
        this.cancelSilenceNudge();
        if (this._reconnectTimer) {
            clearTimeout(this._reconnectTimer);
            this._reconnectTimer = null;
        }
        // Let the spoken goodbye finish before tearing the socket down.
        setTimeout(() => {
            this._transcript.next([]);
            this._lastInputTranscript = '';
            this._lastOutputTranscript = '';
            this._assistantTurnActive = false;
            this._toolCallInFlight = false;
            // reconnect() lands muted (wasMuted === true) → wake-word listening.
            this.reconnect().catch((err) => {
                console.error('[Gemini] end-of-conversation reconnect failed:', err);
            });
        }, 1600);
    }

    private startWakeWordListener(): void {
        this.stopWakeWordListener();
        const SpeechRecognition = (window as any).SpeechRecognition || (window as any).webkitSpeechRecognition;
        if (!SpeechRecognition) {
            console.warn('[Gemini] SpeechRecognition not supported — wake word disabled');
            return;
        }
        const recognition = new SpeechRecognition();
        recognition.continuous = true;
        recognition.interimResults = true;
        recognition.lang = 'en-US';
        recognition.onresult = (event: any) => {
            if (!this._micMuted.value) return;
            for (let i = event.resultIndex; i < event.results.length; i++) {
                const text = String(event.results[i][0]?.transcript ?? '').toLowerCase().trim();
                if (GeminiRealtimeSegment.WAKE_WORDS.some(w => text.includes(w))) {
                    this.zone.run(() => {
                        this.traceService.log('gemini_wake_word', { heard: text },
                            { detail: `Wake word detected: "${text}"` });
                        // Fresh visit: clear filters/search on the kiosk page.
                        this.contextService.notifyVisitStarted();
                        this.setMicMuted(false);
                        this.injectVisitContextBeforeGreeting();
                        setTimeout(() => this.requestWakeGreeting(), 500);
                    });
                    return;
                }
            }
        };
        recognition.onerror = (e: any) => {
            if (e.error === 'no-speech' || e.error === 'aborted') return;
            console.warn('[Gemini] SpeechRecognition error:', e.error);
        };
        recognition.onend = () => {
            if (this._status.value === 'active' && this._micMuted.value) {
                try { recognition.start(); } catch {}
            }
        };
        try {
            recognition.start();
        } catch {}
        this._wakeWordRecognition = recognition;
    }

    private stopWakeWordListener(): void {
        if (this._wakeWordRecognition) {
            try { this._wakeWordRecognition.abort(); } catch {}
            this._wakeWordRecognition = null;
        }
    }

    private playMuteTone(): void {
        try {
            const ctx = new AudioContext();
            const t = ctx.currentTime;
            const gain = ctx.createGain();
            gain.connect(ctx.destination);
            gain.gain.setValueAtTime(0.3, t);
            gain.gain.exponentialRampToValueAtTime(0.001, t + 0.2);

            const osc = ctx.createOscillator();
            osc.type = 'sine';
            osc.frequency.setValueAtTime(660, t);
            osc.frequency.exponentialRampToValueAtTime(440, t + 0.15);
            osc.connect(gain);
            osc.start(t);
            osc.stop(t + 0.2);

            setTimeout(() => ctx.close().catch(() => {}), 500);
        } catch {}
    }

    sendText(text: string): void {
        const trimmed = String(text ?? '').trim();
        if (!trimmed) return;
        if (this._status.value !== 'active' || !this._session) return;

        this.addTranscript('user', trimmed);
        this._aiSpeaking.next(true);
        this.armSilenceNudge();

        this._session.sendClientContent({
            turns: trimmed,
            turnComplete: true,
        });
    }

    private startHealthCheck(): void {
        this.stopHealthCheck();
        this._healthInterval = setInterval(() => {
            if (this._status.value !== 'active') return;

            if (this._inputAudioCtx?.state === 'suspended') {
                this._inputAudioCtx.resume().catch(() => {});
            }
            if (this._outputAudioCtx?.state === 'suspended') {
                this._outputAudioCtx.resume().catch(() => {});
            }

            const now = Date.now();
            const sinceLastMessage = now - this._lastServerMessageAt;
            const sinceLastAudio = now - this._lastAudioSentAt;

            if (this._toolCallInFlight && sinceLastMessage > GeminiRealtimeSegment.STUCK_THRESHOLD_MS) {
                this.traceService.log('gemini_watchdog_reset', { flag: '_toolCallInFlight', sinceLastMessage },
                    { detail: 'Watchdog: reset stuck _toolCallInFlight' });
                this._toolCallInFlight = false;
                this.handleWatchdogRecovery('tool_call_stuck');
            }

            if (this._aiSpeaking.value && sinceLastMessage > GeminiRealtimeSegment.STUCK_THRESHOLD_MS) {
                this.traceService.log('gemini_watchdog_reset', { flag: '_aiSpeaking', sinceLastMessage },
                    { detail: 'Watchdog: reset stuck _aiSpeaking' });
                this._aiSpeaking.next(false);
                this._assistantTurnActive = false;
                this._lastOutputTranscript = '';
                this.handleWatchdogRecovery('ai_speaking_stuck');
            }
        }, GeminiRealtimeSegment.HEALTH_CHECK_MS);
    }

    private stopHealthCheck(): void {
        if (this._healthInterval) {
            clearInterval(this._healthInterval);
            this._healthInterval = null;
        }
    }

    private handleServerMessage(e: any): void {
        this.zone.run(() => {
            this._lastServerMessageAt = Date.now();
            const inputText = String(e?.serverContent?.inputTranscription?.text ?? '').trim();
            if (inputText && inputText !== this._lastInputTranscript) {
                this._lastInputTranscript = inputText;
                this.addTranscript('user', inputText);
                this.armSilenceNudge();
            }

            const modelParts = e?.serverContent?.modelTurn?.parts;
            const partTexts = Array.isArray(modelParts)
                ? modelParts
                      .map((p: any) => String(p?.text ?? '').trim())
                      .filter((t: string) => !!t)
                      .join(' ')
                : '';
            const outputText =
                String(e?.serverContent?.outputTranscription?.text ?? '').trim() ||
                String(e?.text ?? '').trim() ||
                partTexts;

            if (outputText && outputText !== this._lastOutputTranscript) {
                this._lastOutputTranscript = outputText;
                this._lastModelResponseAt = Date.now();
                this.cancelSilenceNudge();

                if (this._assistantTurnActive) {
                    this.appendToLastAssistantEntry(outputText);
                } else {
                    this._assistantTurnActive = true;
                    this.addTranscript('assistant', outputText);
                }
            }

            this.enqueueInlineAudioParts(e);

            if (e?.serverContent?.turnComplete || e?.serverContent?.generationComplete) {
                this._assistantTurnActive = false;
                this._lastOutputTranscript = '';
                this._lastInputTranscript = '';
                this.cancelSilenceNudge();
                this.syncAiSpeakingState();
            }

            const calls = e?.toolCall?.functionCalls;
            if (Array.isArray(calls) && calls.length > 0) {
                this.cancelSilenceNudge();
                this.handleToolCalls(calls);
            }

            const hasAudio = Array.isArray(modelParts) && modelParts.some((p: any) => p?.inlineData?.data);
            if (hasAudio) {
                this._lastModelResponseAt = Date.now();
                this.cancelSilenceNudge();
            }

            if (e?.goAway) {
                this.traceService.log('gemini_goaway',
                    { timeLeft: e.goAway.timeLeft },
                    { detail: 'Server sent GoAway — refreshing session' });
                this.addTranscript('system', 'Refreshing session...');
                this.reconnect().catch(() => {});
            }
        });
    }

    private armSilenceNudge(): void {
        if (this._toolCallInFlight || this.isAgentBusy()) {
            return;
        }
        if (this._silenceNudgeTimer) {
            clearTimeout(this._silenceNudgeTimer);
            this._silenceNudgeTimer = null;
        }
        this._lastUserSpeechAt = Date.now();
        this._silenceNudgeTimer = setTimeout(() => {
            this._silenceNudgeTimer = null;
            if (this._status.value !== 'active') return;

            const sinceModelResponse = Date.now() - this._lastModelResponseAt;
            if (sinceModelResponse < GeminiRealtimeSegment.SILENCE_NUDGE_MS) return;

            this._silenceNudgeCount++;
            this.traceService.log('gemini_silence_detected',
                { nudgeCount: this._silenceNudgeCount, sinceModelResponse },
                { detail: `No response for ${Math.round(sinceModelResponse / 1000)}s — nudge #${this._silenceNudgeCount}` });

            if (this._silenceNudgeCount <= GeminiRealtimeSegment.MAX_SILENCE_NUDGES && this._session) {
                try {
                    this._session.sendClientContent({
                        turns: 'You went silent. In one short sentence, tell the user what you just tried, then ask them how to continue. Do NOT stay silent.',
                        turnComplete: true,
                    });
                } catch (err) {
                    console.error('[Gemini] silence nudge sendClientContent error:', err);
                }
                this.armSilenceNudge();
            } else if (this._silenceNudgeCount > GeminiRealtimeSegment.MAX_SILENCE_NUDGES) {
                this.traceService.log('gemini_silence_reconnect',
                    { nudgeCount: this._silenceNudgeCount },
                    { detail: 'Too many silence nudges — reconnecting session' });
                // Force full recovery: reconnect lands back in listening (not
                // asleep) because the mic was hot when it stalled.
                this._pendingUnmuteAfterReconnect = !this._micMuted.value;
                this.reconnect();
            }
        }, GeminiRealtimeSegment.SILENCE_NUDGE_MS);
    }

    private cancelSilenceNudge(): void {
        if (this._silenceNudgeTimer) {
            clearTimeout(this._silenceNudgeTimer);
            this._silenceNudgeTimer = null;
        }
        this._silenceNudgeCount = 0;
    }

    private handleWatchdogRecovery(reason: 'tool_call_stuck' | 'ai_speaking_stuck'): void {
        const now = Date.now();
        if (now - this._lastWatchdogRecoveryAt < 8000) {
            return;
        }
        this._lastWatchdogRecoveryAt = now;

        try {
            this._session?.sendClientContent({
                turns: 'You froze on that action. In one short sentence, briefly acknowledge and ask the user how to continue. Do NOT stay silent.',
                turnComplete: true,
            });
        } catch {
            // no-op
        }
        // If the model can't unfreeze in a couple seconds, force a reconnect
        // that lands back in listening mode.
        setTimeout(() => {
            if (this._status.value === 'active' && this._aiSpeaking.value === false) {
                return;
            }
            if (this._status.value !== 'active') return;
            this._pendingUnmuteAfterReconnect = !this._micMuted.value;
            this.reconnect().catch(() => {});
        }, 2500);

        this.traceService.log(
            'gemini_watchdog_recovery_prompt',
            { reason },
            { detail: 'Watchdog recovery prompt sent to avoid silent failure' }
        );
    }

    private appendToLastAssistantEntry(newFragment: string): void {
        const t = [...this._transcript.value];
        for (let i = t.length - 1; i >= 0; i--) {
            if (t[i].role === 'assistant') {
                const existing = t[i].text;
                const merged = newFragment.startsWith(existing)
                    ? newFragment
                    : existing + ' ' + newFragment;
                t[i] = { ...t[i], text: merged };
                this._transcript.next(t);
                return;
            }
        }
        this.addTranscript('assistant', newFragment);
    }

    private async handleToolCalls(functionCalls: any[]): Promise<void> {
        this._toolCallInFlight = true;
        this._assistantTurnActive = false;
        this._lastOutputTranscript = '';

        this.traceService.log(
            'gemini_tool_calls',
            { count: functionCalls.length, calls: functionCalls.map(c => ({ name: c?.name, args: c?.args })) },
            { detail: `Gemini tool calls: ${functionCalls.map(c => c?.name).join(', ')}` }
        );

        const responses: { id: string; name: string; response: any }[] = [];

        for (const call of functionCalls) {
            try {
                const name = String(call?.name ?? '');
                const id = String(call?.id ?? '');
                const args = (call?.args && typeof call.args === 'object') ? call.args : {};

                if (name === 'page_action') {
                    const action = String(args.action ?? '').trim();

                    if (action === 'mute_mic' || action === 'mute' || action === 'go_to_sleep') {
                        if (this.contextService.getSnapshot().anonymous) {
                            this.zone.run(() => this.endConversation());
                            responses.push({
                                id,
                                name,
                                response: {
                                    result: 'success',
                                    action: 'end_conversation',
                                    message: 'Visit ended. Say "Yo" to start fresh.',
                                },
                            });
                            continue;
                        }
                        this.zone.run(() => this.setMicMuted(true));
                        responses.push({ id, name, response: { result: 'success', action, message: 'Mic muted. Say "Yo" to resume.' } });
                        continue;
                    }

                    if (
                        action === 'end_conversation' ||
                        action === 'end_session' ||
                        action === 'end_call' ||
                        action === 'finish_conversation'
                    ) {
                        this.zone.run(() => this.endConversation());
                        responses.push({
                            id,
                            name,
                            response: {
                                result: 'success',
                                action,
                                message: 'Conversation ended. Back at the wake word — say "Yo" to start a new visit.',
                            },
                        });
                        continue;
                    }

                    const { action: _a, ...rest } = args;
                    const params = this.flattenActionParamWrappers(rest);

                    const beforeDispatchSnapshot = this.contextService.getSnapshot();
                    const resolved = this.intentRouter.resolvePageAction(
                        beforeDispatchSnapshot,
                        action,
                        params
                    );
                    const effectiveAction = String(resolved.action ?? action).trim();

                    this.traceService.log(
                        'gemini_page_action',
                        {
                            action,
                            effectiveAction,
                            params,
                            corrected: resolved.corrected,
                            confidence: resolved.confidence,
                            unresolved: resolved.unresolved,
                        },
                        {
                            detail: `page_action("${action}" -> "${effectiveAction}", ${JSON.stringify(params)})`,
                        }
                    );

                    const timeoutMs = this.getDispatchTimeoutMsForAction(effectiveAction);
                    const dispatchResult = await this.dispatchPageActionWithTimeout(
                        effectiveAction,
                        params,
                        timeoutMs
                    );
                    const ok = dispatchResult.ok;

                    const snap = this.contextService.getSnapshot();
                    const dataSummary = this.buildToolResponseState(snap, ok);
                    const pageLabel = String(snap?.pageLabel ?? '');
                    const route = String(snap?.route ?? '');
                    const availableActions = this.buildAvailableActionsForToolResponse(snap);

                    this.traceService.log(
                        'gemini_dispatch_result',
                        {
                            action,
                            effectiveAction,
                            dispatched: ok,
                            timedOut: dispatchResult.timedOut,
                        },
                        {
                            detail: dispatchResult.timedOut
                                ? `${effectiveAction} TIMED OUT`
                                : ok
                                ? `${effectiveAction} dispatched OK`
                                : `${effectiveAction} FAILED`,
                        }
                    );

                    if (!ok) {
                        if (dispatchResult.timedOut) {
                            this.addTranscript(
                                'assistant',
                                `I got stuck trying to run "${effectiveAction}". Please repeat the request and I will retry.`
                            );
                        }
                        this.addTranscript(
                            'assistant',
                            resolved.message ||
                                `I could not run "${effectiveAction}" here. Please try again with a different wording.`
                        );
                    }

                    responses.push({
                        id,
                        name,
                        response: ok
                            ? {
                                  result: 'success',
                                  action: effectiveAction,
                                  requested_action: action,
                                  corrected: resolved.corrected,
                                  message: `Action "${effectiveAction}" executed.`,
                                  pageLabel,
                                  route,
                                  availableActions,
                                  dataSummary,
                              }
                            : {
                                  result: 'error',
                                  action: effectiveAction,
                                  requested_action: action,
                                  corrected: resolved.corrected,
                                  timed_out: dispatchResult.timedOut,
                                  pageLabel,
                                  route,
                                  availableActions,
                                  dataSummary,
                                  message:
                                      (dispatchResult.timedOut
                                          ? `Action "${effectiveAction}" timed out while executing.`
                                          : resolved.message) ||
                                      `Action "${effectiveAction}" is not available on "${pageLabel}". Use one of availableActions instead.`,
                              },
                    });
                    continue;
                }

                responses.push({ id, name, response: { result: 'error', message: `Unknown function "${name}".` } });
            } catch (callErr) {
                console.error('[Gemini] error processing tool call:', callErr);
                responses.push({ id: String(call?.id ?? ''), name: String(call?.name ?? ''), response: { result: 'error', message: 'Internal processing error.' } });
            }
        }

        this.sendToolResponses(responses);
    }

    private sendToolResponses(responses: { id: string; name: string; response: any }[]): void {
        try {
            this._session?.sendToolResponse({ functionResponses: responses });
        } catch (err) {
            console.error('[Gemini] sendToolResponse error:', err);
        } finally {
            this._toolCallInFlight = false;
            this.syncAiSpeakingState();
        }
    }

    private buildAvailableActionsForToolResponse(snapshot: any): string[] {
        const actions = Array.isArray(snapshot?.actions) ? snapshot.actions : [];
        const names: string[] = [];
        const seen = new Set<string>();
        for (const a of actions) {
            const name = String(a?.action ?? '').trim();
            if (!name || seen.has(name)) continue;
            seen.add(name);
            names.push(name);
            if (names.length >= 60) break;
        }
        return names;
    }

    /** Return the live kiosk board summary in tool responses. */
    private buildToolResponseState(snapshot: any, ok: boolean): string {
        const full = String(snapshot?.dataSummary ?? '');
        if (!snapshot?.anonymous) {
            return full;
        }
        if (full) {
            return full.length > 6000 ? full.slice(0, 6000) + '…' : full;
        }
        const focus = snapshot?.focus;
        if (focus?.id) {
            const lane = focus?.lane ? ` (${focus.lane})` : '';
            return `Panel open: #${focus.id} "${focus.name || ''}"${lane}.`;
        }
        if (!ok && full) {
            return full.length > 600 ? full.slice(0, 600) + '…' : full;
        }
        return 'No panel open.';
    }

    /**
     * On shared kiosk, periodically reconnect while the mic stays open so
     * conversation history does not balloon and make later turns unreliable.
     */
    private maybeRefreshKioskSession(): void {
        if (this._toolCallInFlight || this.isAgentBusy()) return;
        if (this._anonymousUserTurns < GeminiRealtimeSegment.KIOSK_TURN_REFRESH) return;
        this._anonymousUserTurns = 0;
        this.traceService.log(
            'gemini_kiosk_turn_refresh',
            {},
            { detail: 'Refreshing kiosk session after several user turns' },
        );
        this.reconnect().catch((err) => {
            console.error('[Gemini] kiosk turn refresh failed:', err);
        });
    }

    private async dispatchPageActionWithTimeout(
        action: string,
        params: Record<string, any>,
        timeoutMs: number
    ): Promise<{ ok: boolean; timedOut: boolean }> {
        let timedOut = false;
        const dispatchPromise = this.pageActionService
            .dispatch(action, params)
            .then((ok) => ({ ok, timedOut: false }))
            .catch(() => ({ ok: false, timedOut: false }));
        const timeoutPromise = new Promise<{ ok: boolean; timedOut: true }>((resolve) => {
            setTimeout(() => {
                timedOut = true;
                resolve({ ok: false, timedOut: true });
            }, timeoutMs);
        });
        const result = await Promise.race([dispatchPromise, timeoutPromise]);
        if (timedOut) {
            this.traceService.log(
                'gemini_page_action_timeout',
                { action, timeoutMs },
                { detail: `Timed out waiting for action "${action}"` }
            );
        }
        return result;
    }

    private getDispatchTimeoutMsForAction(action: string): number {
        const normalized = String(action ?? '').trim().toLowerCase();
        const slowActions = new Set([
            'search',
            'refresh',
            'set_search',
            'open_receiving_order',
            'open_order',
        ]);
        return slowActions.has(normalized)
            ? GeminiRealtimeSegment.TOOL_DISPATCH_SLOW_TIMEOUT_MS
            : GeminiRealtimeSegment.TOOL_DISPATCH_TIMEOUT_MS;
    }

    private flattenActionParamWrappers(input: Record<string, any>): Record<string, any> {
        const wrappers = ['actionParams', 'params', 'parameters', 'arguments', 'input'];
        let current: Record<string, any> = { ...(input || {}) };

        for (let i = 0; i < 4; i++) {
            const keys = Object.keys(current);
            if (keys.length === 1) {
                const key = keys[0];
                const value = current[key];
                if (wrappers.includes(key) && value && typeof value === 'object' && !Array.isArray(value)) {
                    current = { ...value };
                    continue;
                }
            }
            break;
        }

        wrappers.forEach((key) => {
            const value = current[key];
            if (value && typeof value === 'object' && !Array.isArray(value)) {
                current = { ...value, ...current };
                delete current[key];
            }
        });

        return current;
    }

    private addTranscript(role: 'user' | 'assistant' | 'system', text: string): void {
        const next = [
            ...this._transcript.value,
            { role, text, at: new Date().toISOString() },
        ].slice(-40);
        this._transcript.next(next);
    }

    private async startMicStreaming(): Promise<void> {
        if (!this._session) {
            return;
        }
        if (this._micStream) {
            return;
        }

        this._micStream = await navigator.mediaDevices.getUserMedia({
            audio: {
                echoCancellation: true,
                noiseSuppression: true,
                autoGainControl: true,
            },
        });
        this.traceService.log(
            'gemini_mic_started',
            {},
            { detail: 'Microphone stream acquired for Gemini Live' },
        );
        this._inputAudioCtx = new AudioContext({ sampleRate: 48000 });
        if (this._inputAudioCtx.state === 'suspended') {
            await this._inputAudioCtx.resume();
        }
        this._inputSource = this._inputAudioCtx.createMediaStreamSource(this._micStream);
        this._inputProcessor = this._inputAudioCtx.createScriptProcessor(4096, 1, 1);

        this._inputProcessor.onaudioprocess = (event) => {
            if (!this._session || this._status.value !== 'active' || this._micMuted.value) {
                return;
            }
            const channel = event.inputBuffer.getChannelData(0);
            const pcm16 = this.floatTo16BitPCM(this.downsampleTo16k(channel, this._inputAudioCtx!.sampleRate));
            const base64 = this.int16ArrayToBase64(pcm16);
            try {
                this._session.sendRealtimeInput({
                    audio: {
                        data: base64,
                        mimeType: 'audio/pcm;rate=16000',
                    },
                });
                this._lastAudioSentAt = Date.now();
            } catch {}
        };

        this._inputSource.connect(this._inputProcessor);
        this._inputProcessor.connect(this._inputAudioCtx.destination);
    }

    private stopMicStreaming(): void {
        if (this._inputProcessor) {
            try {
                this._inputProcessor.disconnect();
            } catch {}
            this._inputProcessor.onaudioprocess = null as any;
            this._inputProcessor = null;
        }
        if (this._inputSource) {
            try {
                this._inputSource.disconnect();
            } catch {}
            this._inputSource = null;
        }
        if (this._inputAudioCtx) {
            try {
                this._inputAudioCtx.close();
            } catch {}
            this._inputAudioCtx = null;
        }
        if (this._micStream) {
            this._micStream.getTracks().forEach((t) => t.stop());
            this._micStream = null;
        }
    }

    private ensureOutputAudioContext(): void {
        if (!this._outputAudioCtx) {
            this._outputAudioCtx = new AudioContext({ sampleRate: 24000 });
            this._nextPlaybackTime = this._outputAudioCtx.currentTime;
        }
        if (this._outputAudioCtx.state === 'suspended') {
            this._outputAudioCtx.resume();
        }
    }

    private closeOutputAudioContext(): void {
        if (!this._outputAudioCtx) {
            return;
        }
        try {
            this._outputAudioCtx.close();
        } catch {}
        this._outputAudioCtx = null;
        this._nextPlaybackTime = 0;
        if (this._playbackEndTimer) {
            clearTimeout(this._playbackEndTimer);
            this._playbackEndTimer = null;
        }
    }

    private enqueueInlineAudioParts(e: any): void {
        const parts = e?.serverContent?.modelTurn?.parts;
        if (!Array.isArray(parts) || parts.length === 0) {
            return;
        }
        for (const part of parts) {
            const inline = part?.inlineData;
            const base64 = String(inline?.data ?? '').trim();
            const mimeType = String(inline?.mimeType ?? inline?.mime_type ?? '').toLowerCase();
            if (!base64) {
                continue;
            }
            this.playPcm16Base64(base64, mimeType);
        }
    }

    private playPcm16Base64(base64Data: string, mimeType: string): void {
        this.ensureOutputAudioContext();
        if (!this._outputAudioCtx) {
            return;
        }

        const sampleRateMatch = mimeType.match(/rate=(\d+)/i);
        const inputSampleRate = sampleRateMatch ? Number(sampleRateMatch[1]) : 24000;
        const bytes = this.base64ToUint8Array(base64Data);
        const sampleCount = Math.floor(bytes.byteLength / 2);
        if (sampleCount <= 0) {
            return;
        }

        const audioBuffer = this._outputAudioCtx.createBuffer(1, sampleCount, inputSampleRate);
        const channel = audioBuffer.getChannelData(0);
        for (let i = 0; i < sampleCount; i++) {
            const lo = bytes[i * 2];
            const hi = bytes[i * 2 + 1];
            let val = (hi << 8) | lo;
            if (val >= 0x8000) val = val - 0x10000;
            channel[i] = Math.max(-1, Math.min(1, val / 32768));
        }

        const src = this._outputAudioCtx.createBufferSource();
        src.buffer = audioBuffer;
        src.connect(this._outputAudioCtx.destination);
        const now = this._outputAudioCtx.currentTime;
        const when = Math.max(now, this._nextPlaybackTime);
        src.start(when);
        this._nextPlaybackTime = when + audioBuffer.duration;
        this._aiSpeaking.next(true);
        this.schedulePlaybackEndCheck();
    }

    /**
     * True while the agent is speaking (local playback) or running a tool.
     * Used for HUD state only — the mic always streams when unmuted so the
     * server can hear the user; gating the mic caused missed speech + nudge loops.
     */
    private isAgentBusy(): boolean {
        if (this._toolCallInFlight) {
            return true;
        }
        if (this._outputAudioCtx) {
            const busyUntil =
                this._nextPlaybackTime + GeminiRealtimeSegment.POST_PLAYBACK_GAP_S;
            if (this._outputAudioCtx.currentTime < busyUntil) {
                return true;
            }
        }
        return false;
    }

    private schedulePlaybackEndCheck(): void {
        if (this._playbackEndTimer) {
            clearTimeout(this._playbackEndTimer);
            this._playbackEndTimer = null;
        }
        if (!this._outputAudioCtx) {
            return;
        }
        const ms = Math.max(
            0,
            (this._nextPlaybackTime +
                GeminiRealtimeSegment.POST_PLAYBACK_GAP_S -
                this._outputAudioCtx.currentTime) *
                1000,
        );
        this._playbackEndTimer = setTimeout(() => {
            this._playbackEndTimer = null;
            this.syncAiSpeakingState();
        }, ms + 20);
    }

    private syncAiSpeakingState(): void {
        const speaking = this.isAgentBusy() && !this._micMuted.value;
        this._aiSpeaking.next(speaking);
    }

    private downsampleTo16k(input: Float32Array, inputSampleRate: number): Float32Array {
        if (inputSampleRate === 16000) {
            return input;
        }
        const ratio = inputSampleRate / 16000;
        const newLength = Math.round(input.length / ratio);
        const result = new Float32Array(newLength);
        let offsetResult = 0;
        let offsetBuffer = 0;
        while (offsetResult < result.length) {
            const nextOffsetBuffer = Math.round((offsetResult + 1) * ratio);
            let accum = 0;
            let count = 0;
            for (let i = offsetBuffer; i < nextOffsetBuffer && i < input.length; i++) {
                accum += input[i];
                count++;
            }
            result[offsetResult] = count > 0 ? accum / count : 0;
            offsetResult++;
            offsetBuffer = nextOffsetBuffer;
        }
        return result;
    }

    private floatTo16BitPCM(input: Float32Array): Int16Array {
        const out = new Int16Array(input.length);
        for (let i = 0; i < input.length; i++) {
            const s = Math.max(-1, Math.min(1, input[i]));
            out[i] = s < 0 ? s * 0x8000 : s * 0x7fff;
        }
        return out;
    }

    private int16ArrayToBase64(data: Int16Array): string {
        const bytes = new Uint8Array(data.buffer, data.byteOffset, data.byteLength);
        let binary = '';
        for (let i = 0; i < bytes.length; i++) {
            binary += String.fromCharCode(bytes[i]);
        }
        return btoa(binary);
    }

    private base64ToUint8Array(base64: string): Uint8Array {
        const binary = atob(base64);
        const bytes = new Uint8Array(binary.length);
        for (let i = 0; i < binary.length; i++) {
            bytes[i] = binary.charCodeAt(i);
        }
        return bytes;
    }

    private armConnectTimeout(attemptId: number): void {
        this.clearConnectTimeout();
        this._connectTimeout = setTimeout(() => {
            if (attemptId !== this._connectAttemptId || this._status.value !== 'connecting') {
                return;
            }
            try {
                this._session?.close?.();
            } catch {}
            this._session = null;
            this._status.next('error');
            this.addTranscript('system', 'Gemini timed out while connecting.');
        }, 45000);
    }

    private clearConnectTimeout(): void {
        if (!this._connectTimeout) {
            return;
        }
        clearTimeout(this._connectTimeout);
        this._connectTimeout = null;
    }

    private canStartOnCurrentPage(snapshot: any): boolean {
        const pageLabel = String(snapshot?.pageLabel ?? '').trim().toLowerCase();
        const route = String(snapshot?.route ?? '').toLowerCase();
        const actionCount = Array.isArray(snapshot?.actions) ? snapshot.actions.length : 0;
        if (actionCount > 0) {
            return true;
        }
        if (pageLabel.includes('receiving') || pageLabel.includes('shipping') || pageLabel.includes('printer') || pageLabel.includes('mailbox') || pageLabel.includes('email')) {
            return true;
        }
        if (route.includes('/receiving') || route.includes('/shipping') || route.includes('/printer-view') || route.includes('/mailbox')) {
            return true;
        }
        return false;
    }
}
